import _ from "lodash"
import React, { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import Modal from "react-modal"
import { defaultModalStyle } from "../../../shared/state/sharedTypes"
import ModalContainer from "../../../shared/components/modals/ModalContainer"
import { IAppState } from "../../../app/appTypes"
import { getAddFormToCaseUI } from "../../../shared/selectors/ui"
import useSearch, { ISearchQuery } from "../../../shared/hooks/useSearch"
import CaseDetails from "../../case/components/details/CaseDetails"
import { ScrollActionTypes } from "../../scroll/state/scrollTypes"
import Pagination from "../../../shared/components/search/Pagination"
import SearchInput from "../../../shared/components/search/SearchInput"
import ResultsPerPage from "../../../shared/components/search/ResultsPerPage"
import { IAddFormToCaseUIState } from "../state/addFormToCaseTypes"
import DetailsCard from "../../../shared/components/layout/detailsCard/DetailsCard"
import DetailsCardInner from "../../../shared/components/layout/detailsCard/DetailsCardInner"
import IconCircleContainer from "../../../shared/components/icons/IconCircleContainer"
import { IconFolder } from "../../../svg/icons"
import { fetchFormsForCaseAsync } from "../state/addFormToCaseActions"
import { useAppDispatch } from "../../../shared/hooks"
import AddFormToCaseList from "./AddFormToCaseList"
import FormTemplatePreview from "./FormTemplatePreview"

interface Props {
  caseId: string
  handleClose: () => void
  isOpen: boolean
}

const AddFormToCaseModal = (props: Props): JSX.Element => {
  if (!props.isOpen) return null

  const { t } = useTranslation(["shared", "forms"])
  const dispatch = useAppDispatch()
  const UI: IAddFormToCaseUIState = useSelector((state: IAppState) => getAddFormToCaseUI(state))
  const initialSearchQuery: ISearchQuery = {
    page: 1,
    searchTerm: UI.searchTerm,
    resultsPerPage: UI.resultsPerPage,
    appliedFilters: []
  }
  const [searchQuery, setSearchQuery] = useSearch(initialSearchQuery, fetchFormsForCaseAsync)
  const [previewingTemplateId, setPreviewingTemplateId] = useState<string | null>(null)

  useEffect(() => {
    dispatch(fetchFormsForCaseAsync(searchQuery))
  }, [searchQuery])

  return (
    <Modal isOpen={props.isOpen} onRequestClose={props.handleClose} style={defaultModalStyle}>
      <ModalContainer handleClose={props.handleClose}>
        <h2 className="text-lg mb-3">
          {t("forms:addFormToCase:title")}
        </h2>

        <DetailsCard>
          <DetailsCardInner>
            <IconCircleContainer className="sm:mr-4">
              <IconFolder className="fill-current w-6 h-6" title={t("shared:case")} />
            </IconCircleContainer>
            <CaseDetails caseId={props.caseId} />
          </DetailsCardInner>
        </DetailsCard>

        <div className="grid grid-rows-2 md:grid-rows-1 flex items-stretch">
          <div className="row-start-2 md:row-start-1 self-center">
            <Trans
              i18nKey="forms:addFormToCase:search"
              components={{
                strong: <strong />
              }}
            />
          </div>
          <div className="row-end-2 md:row-start-1 flex justify-end">
            <ResultsPerPage
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>
        </div>

        <SearchInput
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          buttonText={t("forms:addFormToCase:buttons:search")}
        />

        <h2 className="text-lg mb-2 text-center md:text-left">
          {UI.totalItemsCount} {t("forms:forms")}
        </h2>

       {previewingTemplateId ? (
          <FormTemplatePreview
            formTemplateId={previewingTemplateId}
            onBack={() => setPreviewingTemplateId(null)}
          />
        ) : (
          <AddFormToCaseList
            {...props}
            setPreviewingTemplateId={setPreviewingTemplateId}
          />
        )}

        <Pagination
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          totalItems={UI.totalItemsCount}
          totalPages={UI.totalPages}
          scrollActionType={ScrollActionTypes.UPDATE_MODAL_SCROLL_TO_POSITION}
        />

      </ModalContainer>
    </Modal >
  )
}

export default AddFormToCaseModal
